import { Filter, PanelLeft, PanelRight, Settings, Wrench } from "lucide-react";
import React, { memo, useCallback, useEffect, useState } from "react";
import { useDiaryContext } from "../../../../hooks/useDiaryContext";
import { useDiaryNote } from "../../../../hooks/useDiaryNote";
import { useFeatureFlagContext } from "../../../../hooks/useFeatureFlagContext";
import { useUrlNavigation } from "../../../../hooks/useUrlSearchParams";
import { cn } from "../../../../lib/utils";
import { getBookingList } from "../../../../utils/booking";
import { EDSBadge } from "../../../eds/EDSBadge";
import { EDSButton } from "../../../eds/EDSButton";
import EDSViewTabs from "../../../eds/EDSViewTabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../../ui/dialog";
import { DiaryNoteViewer } from "../DiaryNote/DiaryNoteViewer";
import { DownloadPrintModal } from "../DownloadPrint/DownloadPrintModal";
import DiaryFilters, {
  ISelectedFilters
} from "../Sidebar/DiaryFilters/DiaryFilters";
import {
  ApplyFilter,
  IUserAppliedFilterKeys
} from "../Sidebar/DiaryFilters/DiaryFilterSections";
import { SpaceScannerModal } from "../SpaceScanner/SpaceScannerModal";
import ViewFloorPlanConfig from "../ViewGridConfig/ViewFloorPlanConfig";
import ViewGridConfig from "../ViewGridConfig/ViewGridConfig";
import ViewRunsheetConfig from "../ViewGridConfig/ViewRunsheetConfig";
import { WebViewPopover } from "./WebViewPopover";
import { WebViewTooltip } from "./WebViewTooltip";

export type ToggleView = "DiaryTab" | "FloorplanTab" | "RunsheetTab";

interface SubHeaderProps {
  closedAreasCount?: number;
  onCloseAreasClick?: () => void;
  closeAreasModalVisible?: boolean;
  onRegisterOpenSpaceOptimiser?: (open: () => void) => void;
}

const SubHeader = ({
  closedAreasCount = 0,
  onCloseAreasClick,
  closeAreasModalVisible = false,
  onRegisterOpenSpaceOptimiser
}: SubHeaderProps = {}): React.JSX.Element => {
  const defaultFilters: ISelectedFilters = {
    bookingAttributes: [],
    bookingTypes: [],
    bookingZones: [],
    timeOfDay: [],
    count: 0
  };
  const [isToolsMenuOpen, setIsToolsMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showSpaceOptimiser, setShowSpaceOptimiser] = useState(false);
  const [showDownloadPrint, setShowDownloadPrint] = useState(false);
  const {
    selectedView,
    bookings,
    setDiaryContext,
    selectedVenue,
    showSidebar
  } = useDiaryContext();
  const { updateUrl } = useUrlNavigation();
  const { noteContent, onSaveNote, date } = useDiaryNote();

  const [selectedFilters, setSelectedFilters] =
    useState<ISelectedFilters>(defaultFilters);
  const [userAppliedFilterKeys, setUserAppliedFilterKeys] =
    useState<IUserAppliedFilterKeys>({});
  const { isFlagEnabled } = useFeatureFlagContext();

  const isPrintMenuEnabled = isFlagEnabled("showPrint");

  useEffect(() => {
    if (onRegisterOpenSpaceOptimiser) {
      onRegisterOpenSpaceOptimiser(() => setShowSpaceOptimiser(true));
    }
  }, [onRegisterOpenSpaceOptimiser]);

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [tooltipKey, setTooltipKey] = useState(0);

  const changeView = (destination: ToggleView) => {
    if (selectedVenue && date) {
      updateUrl({
        venueId: selectedVenue,
        tab: destination,
        date: date
      });
    }
  };

  const handleFilterModalToggle = useCallback(() => {
    setShowFilterModal(!showFilterModal);
  }, [showFilterModal]);

  const handleFilterModalClose = useCallback(() => {
    setShowFilterModal(false);
  }, []);

  const toggleSidebar = useCallback(() => {
    // WORKAROUND: Force tooltip to re-render by updating key
    // This is necessary due to a known issue with Radix UI Tooltip component
    // where the tooltip content doesn't update when the button state changes
    // This workaround should be removed when Radix UI fixes the issue or we migrate to a different tooltip solution
    setTooltipKey((prev) => prev + 1);

    // Update the sidebar state
    setDiaryContext({ showSidebar: !showSidebar });
  }, [showSidebar, setDiaryContext]);

  useEffect(() => {
    if (bookings) {
      const bookingsFiltered = getBookingList(
        bookings,
        selectedFilters.timeOfDay,
        selectedFilters.bookingZones,
        selectedFilters.bookingAttributes,
        selectedFilters.bookingTypes
      );
      setDiaryContext({ bookingsFiltered, isInitialising: false });
    }
  }, [
    bookings,
    selectedFilters.timeOfDay,
    selectedFilters.bookingZones,
    selectedFilters.bookingAttributes,
    selectedFilters.bookingTypes
  ]);

  useEffect(() => {
    if (closeAreasModalVisible && isSettingsOpen) {
      setIsSettingsOpen(false);
    }
  }, [closeAreasModalVisible, isSettingsOpen]);

  const getViewClass = (view: ToggleView): string => {
    switch (view) {
      case "DiaryTab":
        return "view-diary";
      case "FloorplanTab":
        return "view-floorplan";
      case "RunsheetTab":
        return "view-runsheet";
      default:
        return "view-runsheet";
    }
  };

  const viewClass = getViewClass(selectedView);

  return (
    <>
      <div
        className={cn(
          "Subheader SubheaderImproved",
          "flex items-center justify-between min-h-[55px] h-auto gap-2 relative z-10 overflow-visible p-2 px-3",
          viewClass
        )}
      >
        {/* Sidebar Toggle Button - positioned to the left of Add Note */}
        {selectedView !== "RunsheetTab" && (
          <WebViewTooltip
            key={`sidebar-toggle-${tooltipKey}`}
            content={showSidebar ? "Hide Bookings" : "Show Bookings"}
            side="bottom"
            sideOffset={8}
          >
            <button
              className={cn("eds-ghost-icon-button", showSidebar && "active")}
              onClick={toggleSidebar}
              aria-label={showSidebar ? "Hide Bookings" : "Show Bookings"}
            >
              {showSidebar ? <PanelLeft /> : <PanelRight />}
            </button>
          </WebViewTooltip>
        )}

        <DiaryNoteViewer
          noteContent={noteContent}
          onSave={onSaveNote}
          date={date}
        />
        <div className="InlineTabsWrapper InlineTabsWrapperEnd flex flex-1 flex-row items-center gap-2.5 justify-end mr-2.5">
          <EDSViewTabs
            selectedView={selectedView}
            onViewChange={changeView}
            showFloorplan
          />
        </div>

        {/* Ghost Icon Button Group */}
        <div className="toolbar-actions flex items-center gap-1 flex-shrink-0 relative z-[11] overflow-visible">
          {/* Tools Button */}
          <WebViewPopover
            open={isToolsMenuOpen}
            onOpenChange={setIsToolsMenuOpen}
            side="bottom"
            sideOffset={8}
            content={
              <div className="p-1">
                <div className="flex flex-col">
                  <button
                    className="flex items-center px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground rounded-sm transition-colors"
                    onClick={() => {
                      setShowSpaceOptimiser(true);
                      setIsToolsMenuOpen(false);
                    }}
                  >
                    Space Optimiser
                  </button>
                  {isPrintMenuEnabled && (
                    <div className="h-px bg-border my-1" />
                  )}
                  {isPrintMenuEnabled && (
                    <button
                      className="flex items-center px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground rounded-sm transition-colors"
                      onClick={() => {
                        setShowDownloadPrint(true);
                        setIsToolsMenuOpen(false);
                      }}
                    >
                      Download/Print
                    </button>
                  )}
                </div>
              </div>
            }
          >
            <WebViewTooltip content="Tools" side="bottom" sideOffset={8}>
              <button
                className={cn(
                  "eds-ghost-icon-button",
                  isToolsMenuOpen && "active"
                )}
                aria-label="Tools"
              >
                <Wrench />
              </button>
            </WebViewTooltip>
          </WebViewPopover>

          {/* Settings Button */}
          <WebViewPopover
            open={isSettingsOpen && !closeAreasModalVisible}
            onOpenChange={setIsSettingsOpen}
            side="bottom"
            sideOffset={8}
            content={
              <div className="p-4">
                {selectedView === "FloorplanTab" && <ViewFloorPlanConfig />}
                {selectedView === "DiaryTab" && !closeAreasModalVisible && (
                  <ViewGridConfig
                    closedAreasCount={closedAreasCount}
                    onCloseAreasClick={onCloseAreasClick}
                  />
                )}
                {selectedView === "RunsheetTab" && <ViewRunsheetConfig />}
                {!selectedView && (
                  <div className="text-sm text-muted-foreground">
                    View settings will appear here
                  </div>
                )}
              </div>
            }
          >
            <WebViewTooltip content="Settings" side="bottom" sideOffset={8}>
              <button
                className={cn(
                  "eds-ghost-icon-button",
                  isSettingsOpen && "active"
                )}
                aria-label="Settings"
              >
                <Settings />
              </button>
            </WebViewTooltip>
          </WebViewPopover>

          {/* Filter Button */}
          <WebViewTooltip
            content={
              selectedFilters.count > 0
                ? `Filter (${selectedFilters.count})`
                : "Filter"
            }
            side="bottom"
            sideOffset={8}
          >
            <div className="filter-button-container relative inline-flex">
              <button
                className={cn(
                  "eds-ghost-icon-button",
                  showFilterModal && "active",
                  selectedFilters.count > 0 && "has-filters"
                )}
                onClick={handleFilterModalToggle}
                aria-label={
                  selectedFilters.count > 0
                    ? `Filter (${selectedFilters.count})`
                    : "Filter"
                }
              >
                <Filter />
              </button>
              {selectedFilters.count > 0 && (
                <EDSBadge
                  variant="default"
                  className="filter-count-badge"
                  aria-label={`${selectedFilters.count} active filters`}
                  style={{
                    position: "absolute",
                    top: "-2px",
                    right: "0px",
                    fontSize: "10px",
                    fontWeight: 700,
                    padding: "0 3px",
                    minWidth: "20px",
                    minHeight: "10px",
                    borderRadius: "6px",
                    zIndex: 50
                  }}
                >
                  {selectedFilters.count}
                </EDSBadge>
              )}
            </div>
          </WebViewTooltip>
        </div>
      </div>

      {/* Filter Modal */}
      <Dialog open={showFilterModal} onOpenChange={setShowFilterModal}>
        <DialogContent className="w-full max-w-none sm:w-3/5 lg:w-2/3 xl:w-3/4 max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>
              Filter {selectedFilters.count > 0 && `(${selectedFilters.count})`}
            </DialogTitle>
          </DialogHeader>
          <div className="filter-modal-content overflow-y-auto flex-1 min-h-0">
            {showFilterModal && (
              <DiaryFilters
                selectedFilters={selectedFilters}
                userAppliedFilterKeys={userAppliedFilterKeys}
                setUserAppliedFilterKeys={setUserAppliedFilterKeys}
              />
            )}
          </div>
          <DialogFooter className="flex-shrink-0">
            <EDSButton
              type="button"
              variant="outline"
              onClick={() => {
                setUserAppliedFilterKeys({});
                setSelectedFilters(defaultFilters);
              }}
            >
              Clear
            </EDSButton>
            <EDSButton
              type="submit"
              onClick={() => {
                ApplyFilter({
                  userAppliedFilterKeys,
                  setSelectedFilters,
                  closeModal: handleFilterModalClose
                });
              }}
            >
              Apply
            </EDSButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Space Optimiser Modal */}
      {showSpaceOptimiser && (
        <SpaceScannerModal
          onClose={() => {
            setShowSpaceOptimiser(false);
          }}
        />
      )}

      {/* Download/Print Modal */}
      {showDownloadPrint && (
        <DownloadPrintModal
          onClose={() => {
            setShowDownloadPrint(false);
          }}
        />
      )}
    </>
  );
};

export default memo(SubHeader);
